import type { RiftboundCard } from '../types';
import { isChampionUnitCard, isGenericSignatureSpell } from './championIdentity';
import { cardTypeList, isTokenSupertype } from './cardTypes';

/** Query string for `/search`, or null when the tag has no catalog facet. */
export type CatalogSearchQuery = Record<string, string>;

type TagCard = Pick<
  RiftboundCard,
  'cardType' | 'cardTypes' | 'supertype' | 'champion' | 'name' | 'tags'
>;

const PRINT_VARIANT: Record<string, string> = {
  'alternate art': 'altArt',
  overnumbered: 'overnumbered',
  signed: 'signed',
  signature: 'signed',
  'champion stamp': 'stampChampion',
  'top 8': 'stampTop8',
  'summoner circle': 'stampSummoner',
  regional: 'stampRegional',
};

export function catalogSearchHref(query: CatalogSearchQuery | null | undefined): string | null {
  if (!query) return null;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const trimmed = value.trim();
    if (trimmed) params.set(key, trimmed);
  }
  const qs = params.toString();
  return qs ? `/search?${qs}` : null;
}

function storedSupertype(card: TagCard, needle: string, fallback: string): string {
  const supertype = (card.supertype || '').trim();
  if (supertype.toLowerCase().includes(needle)) return supertype;
  return fallback;
}

/** Catalog search for the type badge (Legend, Champion Unit, Token Unit, …). */
export function typeTagQuery(card: TagCard): CatalogSearchQuery | null {
  if (isChampionUnitCard(card)) {
    return { type: 'Unit', supertype: storedSupertype(card, 'champion', 'Champion') };
  }
  if (isGenericSignatureSpell(card) && (card.cardType ?? '').toLowerCase() !== 'legend') {
    const types = cardTypeList(card);
    return {
      type: types.join(',') || 'Spell',
      supertype: storedSupertype(card, 'signature', 'Signature'),
    };
  }
  const types = cardTypeList(card);
  if (isTokenSupertype(card)) {
    const supertype = storedSupertype(card, 'token', 'Token');
    if (types.length) return { type: types.join(','), supertype };
    return { type: 'Token' };
  }
  if (!types.length) return null;
  return { type: types.join(',') };
}

export function textFacetQuery(param: 'region' | 'champion' | 'rarity' | 'subtype', value: string): CatalogSearchQuery | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return { [param]: trimmed };
}

/** Print-suffix badges that correspond to a sidebar Variant chip. */
export function printTagQuery(label: string): CatalogSearchQuery | null {
  const variant = PRINT_VARIANT[label.trim().toLowerCase()];
  if (!variant) return null;
  return { variant };
}

/** Preview badge → unreleased sets, which is how the catalog marks previewed cards. */
export function previewTagQuery(unreleasedSetCodes: readonly string[]): CatalogSearchQuery | null {
  const codes = unreleasedSetCodes.map((code) => code.trim()).filter(Boolean);
  if (!codes.length) return null;
  return { set: codes.join(',') };
}

/**
 * Keyword pills in rules text. Skips inline symbols (`[1]`, `[Ready]`, arrows)
 * that are not catalog keyword facets.
 */
export function keywordTagQuery(keyword: string): CatalogSearchQuery | null {
  const trimmed = keyword.trim();
  if (!trimmed) return null;
  const slug = trimmed.toLowerCase().replace(/[\s-]/g, '');
  if (slug === '>' || slug === '&gt;' || slug === 'ready' || /^\d+$/.test(slug)) return null;
  return { keyword: trimmed };
}
